import React from "react"

import { Project } from "shared/types/Project"
import { QueryData } from "shared/types/QueryData"
import { ProjectPhase, ProjectPrice } from "shared/types/__generated__/graphql"

import { PriceListFragment } from "entities/projects/graphql/price-lists/fragments.generated"
import { readonlyProjectPermissions } from "entities/projects/types/ProjectPermissions"
import { TeamRole } from "entities/team-members/types/TeamRole"
import { ProjectModel } from "features/project-management/types/ProjectModel"

import { ProjectPage, ProjectPageModel } from "./ProjectPage"

const LOADING_TEAM_HANDLERS: ProjectModel["team"]["handlers"] = {
  onManageTeam: () => undefined,
  onInviteMember: () => undefined,
  onLeaveTeam: () => undefined
}

export interface ProjectPageLoaderProps
  extends Omit<ProjectPageModel, "projectModel"> {}

/**
 * Renders a ProjectPage with all states in the project model set to loading.
 * Useful for displaying a loading state while data is being fetched.
 */
export const ProjectPageLoader = React.memo((props: ProjectPageLoaderProps) => {
  const loadingProjectModel: ProjectModel = {
    core: {
      data: {
        project: { isLoading: true } as unknown as QueryData<Project>,
        phases: { isLoading: true } as unknown as QueryData<ProjectPhase[]>,
        permissions: { data: readonlyProjectPermissions, isLoading: true },
        role: { isLoading: true } as unknown as QueryData<TeamRole>,
        meta: {
          isPending: true,
          href: "#",
          productTotal: undefined,
          productPositionTotal: undefined
        }
      },
      handlers: {
        onOpen: () => undefined,
        onShowDetails: undefined,
        onEdit: undefined,
        onAddClarificationRequest: undefined,
        onDelete: undefined,
        onActivate: undefined,
        onDuplicate: undefined,
        onShare: undefined,
        onExport: undefined,
        onGaebExport: undefined,
        onMusterheftExport: undefined,
        onOpenProjectHistory: undefined
      }
    },
    team: {
      data: {},
      handlers: LOADING_TEAM_HANDLERS
    },
    pricing: {
      data: {
        price: { isLoading: true } as unknown as QueryData<ProjectPrice>,
        priceLists: { isLoading: true } as unknown as QueryData<
          PriceListFragment[]
        >,
        standardInflationSurcharge: {
          isLoading: true
        } as unknown as QueryData<number>
      }
    },
    attachments: {
      data: {},
      handlers: {
        onOpenAttachments: () => undefined
      }
    },
    creation: {
      handlers: {
        onNewProduct: () => undefined,
        onNewRoom: () => undefined,
        onNewFloor: () => undefined
      }
    },
    clarificationRequest: {
      data: {
        clarificationRequest: null,
        meta: { shouldShowHint: false, lastUpdatedByMember: null }
      },
      handlers: {
        onSubmitContent: () => undefined,
        onDelete: () => undefined,
        onHintClose: () => undefined
      }
    }
  }

  return <ProjectPage {...props} projectModel={loadingProjectModel} />
})
